/**
 * The landing page's <head>: title, description, robots, canonical.
 *
 * public/index.html is one file for the whole SPA and carries the portal's
 * build-time title ("eMerge Platform") and no robots directive. The landing
 * route is also the public site at https://emergesc.com/ (2026-10-06), which
 * must be indexable, while the portal must stay out of search:
 *
 *   emergesc.com / www.emergesc.com   robots "index, follow", canonical https://emergesc.com/
 *   any other host (the portal, dev)  robots "noindex, nofollow", no canonical
 *
 * Set at runtime because only the browser knows which host served the page.
 * Everything is put back on unmount, so the rest of the app is untouched.
 */
import { useEffect } from 'react';

export const LANDING_TITLE =
  'eMerge Senior Care (formerly Sounds for Seniors) | On-site dentistry, podiatry, optometry and audiology for long-term care';
export const LANDING_DESCRIPTION =
  'eMerge Senior Care, formerly Sounds for Seniors, brings on-site dentistry, podiatry, optometry and audiology to assisted living, memory care and skilled nursing communities. Partner with us or refer a resident.';
export const PUBLIC_HOSTS = ['emergesc.com', 'www.emergesc.com'];
export const CANONICAL_URL = 'https://emergesc.com/';

export function isPublicHost(hostname: string): boolean {
  return PUBLIC_HOSTS.includes(String(hostname || '').toLowerCase());
}

/** Set (or create) a <meta name=...>; returns a function that puts it back. */
function setMeta(name: string, content: string): () => void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  const created = !el;
  const previous = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  const node = el;
  return () => {
    if (created) node.remove();
    else if (previous !== null) node.setAttribute('content', previous);
  };
}

function setCanonical(href: string): () => void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  const previous = el ? el.getAttribute('href') : null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  const node = el;
  return () => {
    if (created) node.remove();
    else if (previous !== null) node.setAttribute('href', previous);
  };
}

export function useLandingHead(hostname: string = typeof window !== 'undefined' ? window.location.hostname : '') {
  useEffect(() => {
    const publicHost = isPublicHost(hostname);
    const previousTitle = document.title;
    document.title = LANDING_TITLE;
    const restore = [
      setMeta('description', LANDING_DESCRIPTION),
      setMeta('robots', publicHost ? 'index, follow' : 'noindex, nofollow'),
    ];
    if (publicHost) restore.push(setCanonical(CANONICAL_URL));
    return () => {
      document.title = previousTitle;
      restore.reverse().forEach((fn) => fn());
    };
  }, [hostname]);
}
